import React, { useRef, ChangeEvent } from 'react';
import { Button } from '@/shared/components/ui/Button';
import { Icon } from '@/shared/icons'
interface AvatarUploadProps {
  currentAvatar?: string;
  onAvatarChange: (file: File) => void;
  preview?: string;
}

/**
 * Avatar FiUpload Component
 * Displays current avatar with upload functionality
 *
 * @example
 * <AvatarUpload
 *   currentAvatar={user.avatar}
 *   onAvatarChange={(file) => setAvatar(file)}
 *   preview={previewUrl}
 * />
 */
export const AvatarUpload: React.FC<AvatarUploadProps> = ({
  currentAvatar,
  onAvatarChange,
  preview
}) => {
  const fileInputRef = useRef<HTMLInputElement>(null);

  const handleFileSelect = (e: ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];

    if (!file) return;

    // Validate file type
    if (!file.type.startsWith('image/')) {
      alert('Por favor, selecione apenas arquivos de imagem (JPG, PNG, GIF, WebP)');
      return;
    }

    // Validate file size (5MB max)
    const maxSize = 5 * 1024 * 1024; // 5MB
    if (file.size > maxSize) {
      alert('Arquivo muito grande. O tamanho máximo é 5MB.');
      return;
    }

    onAvatarChange(file);
  };

  const handleButtonClick = () => {
    fileInputRef.current?.click();
  };

  const avatarUrl = preview || currentAvatar;

  return (
    <div className="flex items-center gap-4">
      {/* Avatar Preview Circle */}
      <div
        className="relative w-20 h-20 rounded-full overflow-hidden flex items-center justify-center"
        style={{
          backgroundColor: avatarUrl ? 'transparent' : 'var(--theme-card-border)',
          border: '2px solid var(--theme-card-border)'
        }}
      >
        {avatarUrl ? (
          <img
            src={avatarUrl}
            alt="Avatar"
            className="w-full h-full object-cover"
          />
        ) : (
          <Icon name="user" className="w-10 h-10" style={{ color: 'var(--theme-card-muted)' }} />
        )}
      </div>

      {/* FiUpload Controls */}
      <div className="flex-1">
        <input
          ref={fileInputRef}
          type="file"
          accept="image/*"
          onChange={handleFileSelect}
          className="hidden"
        />
        <Button
          type="button"
          variant="secondary"
          onClick={handleButtonClick}
        >
          <Icon name="upload" className="w-4 h-4 mr-2" />
          Alterar Foto
        </Button>
        <p className="text-xs mt-1" style={{ color: 'var(--theme-card-muted)' }}>
          JPG, PNG, GIF ou WebP (máx. 5MB)
        </p>
      </div>
    </div>
  );
};
